import React, { useState, useEffect, useCallback } from 'react';
import { DefaultEditor } from 'react-simple-wysiwyg';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import PdfFieldMapper from './PdfFieldMapper.jsx';
import { templatePdfUrl } from '../utils/pdfFormTemplate.js';
import './WaiverFormBuilder.css';

// ============================================================
// UNIFIED WAIVER / FORM BUILDER (staff-facing)
// Club chooses how to author each form:
//   • Write / Paste  -> rich-text (HTML) content
//   • Upload PDF     -> their existing document
// Any type: liability, insurance, registration, medical, consent, ...
// ============================================================

const FORM_TYPES = [
  { value: 'liability', label: 'Liability Waiver' },
  { value: 'insurance', label: 'Insurance Form' },
  { value: 'registration', label: 'Registration Form' },
  { value: 'medical', label: 'Medical Release' },
  { value: 'consent', label: 'Photo/Video Consent' },
  { value: 'emergency', label: 'Emergency Contact' },
  { value: 'waiver', label: 'General Waiver' },
  { value: 'other', label: 'Other' },
];
const typeLabel = (v) => (FORM_TYPES.find((t) => t.value === v)?.label || v);

const FIELD_TYPES = ['text', 'textarea', 'email', 'phone', 'date', 'checkbox', 'select'];

const emptyForm = () => ({
  form_name: '',
  form_type: 'liability',
  content: '',
  form_fields: [],
  requires_signature: true,
  requires_annual_renewal: true,
  is_required: true,
  // Opt-in on purpose: uploading a form should not silently put it in front of
  // every family registering at the front desk.
  is_required_for_registration: false,
  version: '1.0',
  effective_date: new Date().toISOString().split('T')[0],
  display_order: 0,
});

function WaiverFormBuilder() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('write'); // 'write' | 'pdf'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [pdfFile, setPdfFile] = useState(null);
  const [existingPdf, setExistingPdf] = useState(null);
  const [newField, setNewField] = useState({ name: '', type: 'text', required: true });
  // Template id currently open in the PDF field mapper.
  const [mappingId, setMappingId] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/forms/templates');
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      // Show what the server actually said — a swallowed reason here cost real
      // debugging time when a migration had not been applied.
      toast.error(err.response?.data?.error || 'Could not load templates');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setMode('write');
    setPdfFile(null);
    setExistingPdf(null);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    let fields = [];
    try { fields = typeof t.form_fields === 'string' ? JSON.parse(t.form_fields) : (t.form_fields || []); } catch { fields = []; }
    setForm({
      form_name: t.form_name || '',
      form_type: t.form_type || 'liability',
      content: t.content || '',
      form_fields: Array.isArray(fields) ? fields : [],
      requires_signature: !!t.requires_signature,
      requires_annual_renewal: !!t.requires_annual_renewal,
      is_required: !!t.is_required,
      is_required_for_registration: !!t.is_required_for_registration,
      version: t.version || '1.0',
      effective_date: t.effective_date ? String(t.effective_date).split('T')[0] : new Date().toISOString().split('T')[0],
      display_order: t.display_order ?? 0,
    });
    setMode(t.pdf_file_path ? 'pdf' : 'write');
    setExistingPdf(t.pdf_file_path || null);
    setPdfFile(null);
    setShowModal(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addField = () => {
    if (!newField.name.trim()) return;
    set('form_fields', [...form.form_fields, { ...newField, name: newField.name.trim() }]);
    setNewField({ name: '', type: 'text', required: true });
  };
  const removeField = (i) => set('form_fields', form.form_fields.filter((_, idx) => idx !== i));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.form_name.trim()) { toast.error('Give the form a name'); return; }
    setSaving(true);
    try {
      if (mode === 'pdf' && !editingId) {
        // Create a PDF-based template (multipart)
        if (!pdfFile) { toast.error('Choose a PDF to upload'); setSaving(false); return; }
        const fd = new FormData();
        fd.append('waiver_pdf', pdfFile);
        fd.append('formName', form.form_name);
        fd.append('formType', form.form_type);
        fd.append('requiresSignature', form.requires_signature);
        fd.append('requiresAnnualRenewal', form.requires_annual_renewal);
        fd.append('isRequired', form.is_required);
        fd.append('isRequiredForRegistration', form.is_required_for_registration);
        fd.append('version', form.version);
        fd.append('effectiveDate', form.effective_date);
        fd.append('displayOrder', form.display_order || 0);
        const up = await api.post('/api/forms/upload-pdf-waiver', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          // Field detection walks every page of the document before responding.
          timeout: 90_000,
        });
        // An uploaded PDF is not usable until its blanks are mapped, so go
        // straight into the mapper rather than leaving it half-configured.
        const d = up.data?.detected;
        setShowModal(false);
        loadTemplates();
        if (up.data?.templateId) {
          toast.success(
            d?.fieldCount
              ? `Found ${d.fieldCount} fillable field${d.fieldCount === 1 ? '' : 's'} — review them now`
              : 'Uploaded. No blanks detected — place the fields by hand.'
          );
          setMappingId(up.data.templateId);
        }
        setSaving(false);
        return;
      } else {
        // Create/update a written-content template, or edit a PDF template's metadata
        const body = {
          form_name: form.form_name,
          form_type: form.form_type,
          content: mode === 'write' ? form.content : undefined,
          form_fields: form.form_fields,
          requires_signature: form.requires_signature,
          requires_annual_renewal: form.requires_annual_renewal,
          is_required: form.is_required,
          is_required_for_registration: form.is_required_for_registration,
          version: form.version,
          effective_date: form.effective_date,
          display_order: Number(form.display_order) || 0,
        };
        if (editingId) {
          await api.put(`/api/forms/templates/${editingId}`, body);
        } else {
          await api.post('/api/forms/templates', body);
        }
      }
      toast.success(editingId ? 'Form updated' : 'Form created');
      setShowModal(false);
      loadTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      toast.error('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── kiosk lineup ──
  // Attach/detach and reorder without opening the edit dialog: managing which
  // forms a family is handed is a routine task, not a form-editing one.
  const setKiosk = async (t, on) => {
    try {
      // Appending means "next free position"; removing is simply 0.
      const nextOrder = on ? kioskLineup.length + 1 : 0;
      await api.put(`/api/forms/templates/${t.id}`, { display_order: nextOrder });
      toast.success(on ? `“${t.form_name}” added to the kiosk` : `“${t.form_name}” removed from the kiosk`);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update the kiosk lineup');
    }
  };

  const moveInKiosk = async (t, direction) => {
    const lineup = kioskLineup;
    const i = lineup.findIndex((x) => x.id === t.id);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= lineup.length) return;
    const other = lineup[j];
    // Positions can collide (everything defaults to 0), so write explicit
    // sequential values rather than swapping whatever happens to be stored.
    try {
      await Promise.all([
        api.put(`/api/forms/templates/${t.id}`, { display_order: j + 1 }),
        api.put(`/api/forms/templates/${other.id}`, { display_order: i + 1 }),
      ]);
      loadTemplates();
    } catch (err) {
      toast.error('Could not reorder');
    }
  };

  const archive = async (t) => {
    if (!window.confirm(`Archive "${t.form_name}"? It stays on record but stops being required.`)) return;
    try {
      await api.delete(`/api/forms/templates/${t.id}`);
      toast.success('Archived');
      loadTemplates();
    } catch (err) {
      toast.error('Could not archive');
    }
  };

  const active = templates.filter((t) => t.is_active);
  const archived = templates.filter((t) => !t.is_active);
  // Exactly what the kiosk shows, in the order it shows it.
  const kioskLineup = active
    .filter((t) => (t.display_order ?? 0) > 0)
    .sort(
      (a, b) =>
        (a.display_order ?? 0) - (b.display_order ?? 0) ||
        String(a.form_name).localeCompare(String(b.form_name))
    );

  return (
    <div className="wfb">
      <div className="wfb-head">
        <div>
          <h2>Waiver &amp; Form Templates</h2>
          <p className="wfb-sub">Upload a PDF or write/paste the text — your choice, per form.</p>
        </div>
        <button className="wfb-btn wfb-btn-primary" onClick={openCreate}>+ New Waiver / Form</button>
      </div>

      {/* What a family actually gets handed at the front desk, in order. */}
      {!loading && (
        <section className="wfb-kiosk">
          <div className="wfb-kiosk-head">
            <h3>🖥️ Registration kiosk lineup</h3>
            <span className="wfb-kiosk-count">
              {kioskLineup.length === 0
                ? 'nothing attached'
                : `${kioskLineup.length} form${kioskLineup.length === 1 ? '' : 's'}`}
            </span>
          </div>
          {kioskLineup.length === 0 ? (
            <p className="wfb-kiosk-empty">
              No forms are collected at the kiosk yet. Use <strong>+ Add to kiosk</strong> on any
              form below to include it — families will be asked to sign it during registration.
            </p>
          ) : (
            <ol className="wfb-kiosk-list">
              {kioskLineup.map((t, i) => (
                <li key={t.id}>
                  <span className="wfb-kiosk-name">
                    {t.form_name}
                    <em>{typeLabel(t.form_type)}</em>
                    <em>#{t.display_order}</em>
                    {t.pdf_file_path && t.field_map_status !== 'confirmed' && (
                      <span className="wfb-kiosk-flag">⚠️ not fillable yet</span>
                    )}
                  </span>
                  <span className="wfb-kiosk-actions">
                    <button title="Move earlier" disabled={i === 0} onClick={() => moveInKiosk(t, -1)}>↑</button>
                    <button title="Move later" disabled={i === kioskLineup.length - 1} onClick={() => moveInKiosk(t, 1)}>↓</button>
                    <button className="wfb-kiosk-remove" onClick={() => setKiosk(t, false)}>Remove</button>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {loading ? (
        <div className="wfb-empty">Loading…</div>
      ) : active.length === 0 ? (
        <div className="wfb-empty">
          No forms yet. Click <strong>New Waiver / Form</strong> to upload a PDF or write one.
        </div>
      ) : (
        <div className="wfb-grid">
          {active.map((t) => (
            <div key={t.id} className="wfb-card">
              <div className="wfb-card-top">
                <span className={`wfb-type wfb-type-${t.form_type}`}>{typeLabel(t.form_type)}</span>
                <span className="wfb-source">{t.pdf_file_path ? '📄 PDF' : '✍️ Written'}</span>
              </div>
              <h3 className="wfb-name">{t.form_name}</h3>
              <div className="wfb-chips">
                {t.is_required ? <span className="wfb-chip req">Required</span> : <span className="wfb-chip">Optional</span>}
                {/* Whether a family is handed this form at the front desk. */}
                {(t.display_order ?? 0) > 0 && <span className="wfb-chip kiosk">🖥️ At kiosk #{t.display_order}</span>}
                {!!t.requires_signature && <span className="wfb-chip">✍️ Signature</span>}
                {!!t.requires_annual_renewal && <span className="wfb-chip">📅 Annual</span>}
                {t.version && <span className="wfb-chip">v{t.version}</span>}
                {/* A PDF form is not usable at the kiosk until its blanks are
                    mapped and confirmed — say so plainly on the card. */}
                {t.pdf_file_path && (
                  t.field_map_status === 'confirmed'
                    ? <span className="wfb-chip ok">✅ Fillable</span>
                    : t.field_map_status === 'detected'
                      ? <span className="wfb-chip warn">⚠️ Needs review</span>
                      : <span className="wfb-chip warn">⚠️ Not mapped</span>
                )}
              </div>
              {t.pdf_file_path && t.field_map_status !== 'confirmed' && (
                <p className="wfb-mapnote">
                  Parents can’t fill this in yet — map its fields to turn it on.
                </p>
              )}
              <div className="wfb-card-actions">
                <button
                  className={(t.display_order ?? 0) > 0 ? 'wfb-btn wfb-btn-ghost' : 'wfb-btn wfb-btn-kiosk'}
                  onClick={() => setKiosk(t, !((t.display_order ?? 0) > 0))}
                >
                  {(t.display_order ?? 0) > 0 ? '🖥️ Remove from kiosk' : '+ Add to kiosk'}
                </button>
                {t.pdf_file_path && (
                  <button className="wfb-btn wfb-btn-primary" onClick={() => setMappingId(t.id)}>
                    {t.field_map_status === 'confirmed' ? 'Edit fields' : 'Map fields'}
                  </button>
                )}
                {templatePdfUrl(t, API_BASE_URL) && (
                  <a className="wfb-btn wfb-btn-ghost" href={templatePdfUrl(t, API_BASE_URL)} target="_blank" rel="noopener noreferrer">View PDF</a>
                )}
                <button className="wfb-btn wfb-btn-ghost" onClick={() => openEdit(t)}>Edit</button>
                <button className="wfb-btn wfb-btn-danger" onClick={() => archive(t)}>Archive</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <details className="wfb-archived">
          <summary>{archived.length} archived form{archived.length !== 1 ? 's' : ''}</summary>
          <ul>
            {archived.map((t) => (
              <li key={t.id}>
                {t.form_name} <span className="wfb-muted">({typeLabel(t.form_type)})</span>
                <button className="wfb-linkbtn" onClick={() => api.put(`/api/forms/templates/${t.id}`, { is_active: 1 }).then(loadTemplates)}>Restore</button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {showModal && (
        <div className="wfb-overlay" onClick={() => setShowModal(false)}>
          <div className="wfb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wfb-modal-head">
              <h2>{editingId ? 'Edit Form' : 'New Waiver / Form'}</h2>
              <button className="wfb-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSave} className="wfb-form">
              {/* Method toggle (locked to the existing method when editing) */}
              <div className="wfb-method">
                <button
                  type="button"
                  className={mode === 'write' ? 'active' : ''}
                  onClick={() => !editingId && setMode('write')}
                  disabled={!!editingId && mode !== 'write'}
                >✍️ Write / Paste</button>
                <button
                  type="button"
                  className={mode === 'pdf' ? 'active' : ''}
                  onClick={() => !editingId && setMode('pdf')}
                  disabled={!!editingId && mode !== 'pdf'}
                >📄 Upload PDF</button>
              </div>

              <div className="wfb-row">
                <div className="wfb-field">
                  <label>Form Name *</label>
                  <input type="text" value={form.form_name} onChange={(e) => set('form_name', e.target.value)}
                    placeholder="e.g., 2026 Liability Waiver" required />
                </div>
                <div className="wfb-field">
                  <label>Type *</label>
                  <select value={form.form_type} onChange={(e) => set('form_type', e.target.value)}>
                    {FORM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Authoring area */}
              {mode === 'write' ? (
                <div className="wfb-field">
                  <label>Form Content *</label>
                  <DefaultEditor value={form.content} onChange={(e) => set('content', e.target.value)} />
                  <small className="wfb-hint">Paste your existing waiver text or type it. Formatting (bold, lists, headings) is preserved for parents at signing.</small>
                </div>
              ) : (
                <div className="wfb-field">
                  <label>{editingId ? 'PDF Document' : 'Upload PDF *'}</label>
                  {editingId ? (
                    <div className="wfb-pdf-note">
                      {existingPdf ? <>Current file: <code>{existingPdf.split('/').pop()}</code>. To replace the file, archive this and upload a new one. Metadata below is editable.</> : 'No PDF on file.'}
                    </div>
                  ) : (
                    <>
                      <input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files[0] || null)} />
                      {pdfFile && <small className="wfb-hint">Selected: {pdfFile.name}</small>}
                    </>
                  )}
                </div>
              )}

              {/* Options */}
              <div className="wfb-opts">
                <label><input type="checkbox" checked={form.requires_signature} onChange={(e) => set('requires_signature', e.target.checked)} /> Requires signature</label>
                <label><input type="checkbox" checked={form.is_required} onChange={(e) => set('is_required', e.target.checked)} /> Required to participate (tracked on compliance)</label>
                <label><input type="checkbox" checked={form.requires_annual_renewal} onChange={(e) => set('requires_annual_renewal', e.target.checked)} /> Expires after 1 year (annual renewal)</label>
              </div>

              <div className="wfb-row">
                <div className="wfb-field">
                  <label>Version</label>
                  <input type="text" value={form.version} onChange={(e) => set('version', e.target.value)} placeholder="1.0" />
                </div>
                <div className="wfb-field">
                  <label>Effective Date</label>
                  <input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} />
                </div>
                <div className="wfb-field">
                  <label>Kiosk order — 0 = not used at kiosk</label>
                  {/* One control: 0 keeps the form out of registration entirely,
                      1..n places it in the run families are handed. */}
                  <input
                    type="number"
                    min="0"
                    value={form.display_order}
                    onChange={(e) => set('display_order', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Optional extra fields (useful for registration/medical forms) */}
              <details className="wfb-fields">
                <summary>Extra fields to collect ({form.form_fields.length})</summary>
                <div className="wfb-field-add">
                  <input type="text" placeholder="Field label (e.g., Allergies)" value={newField.name}
                    onChange={(e) => setNewField({ ...newField, name: e.target.value })} />
                  <select value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })}>
                    {FIELD_TYPES.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
                  </select>
                  <label className="wfb-inline"><input type="checkbox" checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} /> req</label>
                  <button type="button" className="wfb-btn wfb-btn-ghost" onClick={addField}>Add</button>
                </div>
                {form.form_fields.length > 0 && (
                  <ul className="wfb-field-list">
                    {form.form_fields.map((f, i) => (
                      <li key={i}><span>{f.name} <em>({f.type}{f.required ? ', required' : ''})</em></span>
                        <button type="button" className="wfb-x" onClick={() => removeField(i)}>×</button></li>
                    ))}
                  </ul>
                )}
              </details>

              <div className="wfb-modal-foot">
                <button type="button" className="wfb-btn wfb-btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="wfb-btn wfb-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Form'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mappingId && (
        <PdfFieldMapper
          templateId={mappingId}
          onClose={() => setMappingId(null)}
          onSaved={loadTemplates}
        />
      )}
    </div>
  );
}

export default WaiverFormBuilder;
